import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowRight, Sparkles, Target, BookOpen, Wind, Eye, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Target className="w-6 h-6" strokeWidth={1.5} />,
      title: "Identity-First Goals",
      description: "Define who you're becoming, not just what you're achieving."
    },
    {
      icon: <Wind className="w-6 h-6" strokeWidth={1.5} />,
      title: "Daily Breathwork",
      description: "Activate your parasympathetic nervous system and prepare your mind for intentional change."
    },
    {
      icon: <Sparkles className="w-6 h-6" strokeWidth={1.5} />,
      title: "Personalised Affirmations",
      description: "Reinforce your future identity with affirmations created around your goals."
    },
    {
      icon: <Eye className="w-6 h-6" strokeWidth={1.5} />,
      title: "Personalised Visualisation",
      description: "Immerse yourself in a guided visualisation designed around your future self."
    },
    {
      icon: <Heart className="w-6 h-6" strokeWidth={1.5} />,
      title: "Daily Gratitude Practice",
      description: "Build awareness of progress, possibility and the positive moments in each day."
    },
    {
      icon: <BookOpen className="w-6 h-6" strokeWidth={1.5} />,
      title: "Reflective Journaling",
      description: "Capture insights and track your transformation journey."
    }
  ];

  return (
    <div className="min-h-screen bg-[#1C1C1E] overflow-hidden">
      {/* Hero Section */}
      <div className="relative">
        {/* Subtle glow effect */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#8BAA9E]/10 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="container mx-auto px-6 pt-12 pb-20">
          {/* Header */}
          <header className="flex items-center justify-between mb-20 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#8BAA9E]/20 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-[#8BAA9E]" strokeWidth={1.5} />
              </div>
              <span className="font-serif text-xl text-[#DCD3CA]">FutureSelf</span>
            </div>
            <Button 
              variant="ghost" 
              className="text-[#B5B1AB] hover:text-[#DCD3CA]"
              onClick={() => navigate('/login')}
              data-testid="header-login-btn"
            >
              Sign In
            </Button>
          </header>

          {/* Main Hero */}
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#8BAA9E] text-sm tracking-[0.3em] uppercase mb-6 animate-fade-in stagger-1 font-sans">
              Identity-Based Transformation
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#DCD3CA] mb-8 leading-tight animate-slide-up stagger-2">
              Become the Person<br />
              <span className="text-[#8BAA9E]">You're Meant to Be</span>
            </h1>
            <p className="text-[#B5B1AB] text-lg md:text-xl leading-relaxed mb-12 animate-slide-up stagger-3 font-sans font-light">
              Transform your habits by aligning daily actions with your future identity. 
              Visualise, journal, and track your journey to self-actualisation.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up stagger-4">
              <Button 
                className="bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full px-8 py-6 text-base font-medium flex items-center gap-2 w-full sm:w-auto"
                onClick={() => navigate('/register')}
                data-testid="get-started-btn"
              >
                Begin Your Journey
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button 
                variant="outline"
                className="border-[#B8AFA6] text-[#DCD3CA] hover:bg-[#B8AFA6]/10 rounded-full px-8 py-6 text-base w-full sm:w-auto"
                onClick={() => navigate('/login')}
                data-testid="login-btn"
              >
                I Have an Account
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section (The Method) */}
      <section className="py-20 bg-[#252528]/30">
        <div className="container mx-auto px-6">
          <p className="text-[#8BAA9E] text-xs tracking-[0.3em] uppercase text-center mb-4 font-sans">
            The Method
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#DCD3CA] text-center mb-16">
            Intentional Growth
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {features.map((feature) => (
              <div 
                key={feature.title}
                data-testid={`method-card-${feature.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="bg-[#1C1C1E] border border-white/5 rounded-[20px] p-8 hover:border-[#8BAA9E]/20 transition-all duration-500 flex flex-col"
              >
                <div className="w-12 h-12 rounded-full bg-[#8BAA9E]/10 flex items-center justify-center mb-6 text-[#8BAA9E]">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-xl text-[#DCD3CA] mb-3">{feature.title}</h3>
                <p className="text-[#B5B1AB] font-sans font-light leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Identity Statement Preview Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-md mx-auto animate-slide-up stagger-5">
            <div className="bg-[#252528] border border-white/5 rounded-[24px] p-8 shadow-xl" data-testid="identity-statement-preview">
              <p className="text-[#8BAA9E] text-xs tracking-[0.2em] uppercase mb-4 font-sans">
                Your Identity Statement
              </p>
              <p className="font-serif text-2xl text-[#DCD3CA] leading-snug mb-6">
                "I am a person who consistently shows up for their dreams, 
                taking aligned action every single day."
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-white/5">
                <div>
                  <p className="text-[#7A7670] text-xs uppercase tracking-wider mb-1 font-sans">Current Streak</p>
                  <p className="text-[#C6A75E] text-2xl font-serif">14 days</p>
                </div>
                <div className="w-16 h-16 rounded-full border-2 border-[#8BAA9E] flex items-center justify-center">
                  <span className="text-[#8BAA9E] text-lg font-serif">87%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#DCD3CA] mb-6">
            Start Your Transformation
          </h2>
          <p className="text-[#B5B1AB] max-w-xl mx-auto mb-10 font-sans font-light">
            Join thousands who are becoming their future selves, one aligned action at a time.
          </p>
          <Button 
            className="bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full px-10 py-6 text-base font-medium"
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-btn"
          >
            Create Free Account
          </Button>
          <p className="text-[#7A7670] text-sm mt-4 font-sans">
            Free forever • No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-4 h-4 text-[#8BAA9E]" strokeWidth={1.5} />
            <span className="text-[#7A7670] text-sm font-sans">FutureSelf</span>
          </div>
          <p className="text-[#7A7670] text-sm font-sans">© 2024 All rights reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
